import { get } from "../client";
import {
  GetAgenciesResponse,
  GetRunsResponse,
  GetUsersInfoResponse,
  UserSearchResult,
  DefaultResponse,
  GetSalesMetricsResponse,
  GetSalesRepsResponse,
  GetIssueCategoriesResponse,
  GetIssuesResponse,
  GetCategoryHistoryResponse,
  CollectIssuesResponse,
  RecalculateCategoriesResponse,
  UsageComparisonParams,
  PolicyInsightsParams,
  PolicyInsightsResponseDTO,
  UsageIterationsParams,
  UsageOverviewParams,
  UsageOverviewResponseDTO,
  WorkflowIterationsResponseDTO,
  UsageComparisonResponseDTO,
  UsageTimeseriesParams,
  UsageTimeseriesResponseDTO,
  AgentMessage,
  AgentTurnResponse,
  ChatDetail,
  ListChatsResponse,
  PrDashboardResponse,
  OperatingCostEntry,
  OperatingCostsResponse,
} from "./types";
import {
  adminTokenHeader,
  clearAdminToken,
  isInvalidAdminTokenError,
} from "../../auth/adminTokenStore";

const BASE_URL = process.env.REACT_APP_BE_URL as string;

const jsonHeaders = () => ({
  ...adminTokenHeader(),
  "Content-Type": "application/json",
});

const tokenOnlyHeaders = adminTokenHeader;

function handleInvalidAdminToken<T>(resp: { status: number; body: T }) {
  if (isInvalidAdminTokenError(resp.status, resp.body)) {
    clearAdminToken();
  }
  return resp;
}

const adminApi = {
  adminValidateToken: async () => {
    return await get<{ valid: boolean }>(`${BASE_URL}/admin-validate-token`, {
      method: "POST",
      headers: jsonHeaders(),
      body: JSON.stringify({}),
    });
  },
  adminGetAgencies: async (agency_id?: string | null) => {
    return handleInvalidAdminToken(
      await get<GetAgenciesResponse>(`${BASE_URL}/admin-retrieve-agencies`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agency_id }),
      })
    );
  },
  adminGetAgencyRuns: async (agencyId: string) => {
    return handleInvalidAdminToken(
      await get<GetRunsResponse>(`${BASE_URL}/admin-retrieve-agency-runs`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agencyId }),
      })
    );
  },
  adminGetAgencyUsersInfo: async (agencyId: string, selectedMonth: string | null = null) => {
    return handleInvalidAdminToken(
      await get<GetUsersInfoResponse>(`${BASE_URL}/admin-retrieve-agency-user-info`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          agency_id: agencyId,
          selected_month: selectedMonth,
          // Show internal (+something@coverflow.com) accounts so admins can log in as them too.
          include_test_accounts: true,
        }),
      })
    );
  },
  adminUpdateUser: async (userId: string, data: Record<string, any>) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-update-user`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ user_id: userId, ...data }),
      })
    );
  },
  adminCreateTeam: async (agencyId: string, teamName: string) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-create-team`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agencyId, team_name: teamName }),
      })
    );
  },
  adminInviteUsers: async (agencyId: string, emails: string[]) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-invite-users`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agencyId, emails: emails }),
      })
    );
  },
  adminDeleteUser: async (userId: string) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-delete-user`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ user_id: userId }),
      })
    );
  },
  adminCreateAccessToken: async (agencyId: string, accessToken: string) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-create-access-token`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agencyId, access_token: accessToken }),
      })
    );
  },
  adminUpdateAgency: async (agencyId: string, data: Record<string, any>) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-update-agency`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ agency_id: agencyId, ...data }),
      })
    );
  },
  adminCreateAgency: async (name: string, totalCredits: number) => {
    return handleInvalidAdminToken(
      await get<DefaultResponse>(`${BASE_URL}/admin-create-agency`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ name: name, max_credits: totalCredits }),
      })
    );
  },
  adminGetUsage: async function* () {
    const response = await fetch(`${BASE_URL}/admin-retrieve-usage`, {
      method: "POST",
      credentials: "include",
      headers: tokenOnlyHeaders(),
    });
    if (response.status === 403) {
      const body = await response.json().catch(() => ({}));
      if (isInvalidAdminTokenError(response.status, body)) {
        clearAdminToken();
        return;
      }
    }

    if (!response.ok) {
      console.error("Failed to fetch usage data:", response.status, response.statusText);
      return;
    }

    if (!response.body) {
      console.error("No response body for usage data");
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let startIdx = 0;
        while (true) {
          const openIdx = buffer.indexOf("{", startIdx);
          if (openIdx === -1) break;
          let braceCount = 0;
          let endIdx = openIdx;
          for (; endIdx < buffer.length; endIdx++) {
            if (buffer[endIdx] === "{") braceCount++;
            else if (buffer[endIdx] === "}") braceCount--;
            if (braceCount === 0) break;
          }
          if (braceCount !== 0) break;

          const jsonStr = buffer.slice(openIdx, endIdx + 1);
          try {
            yield JSON.parse(jsonStr);
          } catch (e) {
            console.error("Failed to parse JSON:", jsonStr, e);
          }
          startIdx = endIdx + 1;
        }
        buffer = buffer.slice(startIdx);
      }
    } catch (err) {
      console.error("Stream reading error:", err);
    }
  },
  adminSearchUsers: async (q: string) => {
    return handleInvalidAdminToken(
      await get<{ users: UserSearchResult[] }>(`${BASE_URL}/admin-search-users`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ q }),
      })
    );
  },
  adminGetUserExport: async (startDate?: string | null, endDate?: string | null) => {
    const body: any = {};
    if (startDate) {
      body.start_date = startDate;
    }
    if (endDate) {
      body.end_date = endDate;
    }
    return handleInvalidAdminToken(
      await get<{ users: any[] }>(`${BASE_URL}/admin-retrieve-user-export`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(body),
      })
    );
  },
  adminGetUsageExport: async (startDate?: string | null, endDate?: string | null) => {
    const body: any = {};
    if (startDate) {
      body.start_date = startDate;
    }
    if (endDate) {
      body.end_date = endDate;
    }
    return handleInvalidAdminToken(
      await get<{ agencies: any[] }>(`${BASE_URL}/admin-retrieve-usage-export`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(body),
      })
    );
  },
  adminGetRunningWorkflows: async (startDate?: string | null, endDate?: string | null) => {
    return handleInvalidAdminToken(
      await get(`${BASE_URL}/admin-retrieve-running-workflows`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        }),
      })
    );
  },
  adminMarkWorkflowFailed: async (workflowType: string, workflowId: string, reason?: string) => {
    return handleInvalidAdminToken(
      await get(`${BASE_URL}/admin-mark-workflow-failed`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          workflow_type: workflowType,
          workflow_id: workflowId,
          reason: reason,
        }),
      })
    );
  },
  adminRetryWorkflow: async (workflowType: string, workflowId: string, userId?: string) => {
    return handleInvalidAdminToken(
      await get(`${BASE_URL}/admin-retry-workflow`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          workflow_type: workflowType,
          workflow_id: workflowId,
          user_id: userId,
        }),
      })
    );
  },
  adminGetDownIndicators: async () => {
    return handleInvalidAdminToken(
      await get<{ openai: boolean; anthropic: boolean }>(`${BASE_URL}/get-down-indicator`, {
        method: "GET",
        headers: jsonHeaders(),
      })
    );
  },
  adminSetDownIndicator: async (resource: "openai" | "anthropic", down: boolean) => {
    return handleInvalidAdminToken(
      await get(`${BASE_URL}/set-down-indicator`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ resource, down }),
      })
    );
  },
  adminGetSalesMetrics: async (startDate?: string, endDate?: string, rep?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    if (rep) params.set("rep", rep);
    const qs = params.toString();
    return handleInvalidAdminToken(
      await get<GetSalesMetricsResponse>(`${BASE_URL}/admin/sales-metrics${qs ? `?${qs}` : ""}`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminGetSalesReps: async () => {
    return handleInvalidAdminToken(
      await get<GetSalesRepsResponse>(`${BASE_URL}/admin/sales-metrics/reps`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminGetIssueCategories: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    const qs = params.toString();
    return handleInvalidAdminToken(
      await get<GetIssueCategoriesResponse>(
        `${BASE_URL}/admin/issues/categories${qs ? `?${qs}` : ""}`,
        { method: "GET", headers: tokenOnlyHeaders() }
      )
    );
  },
  adminGetIssues: async (params?: {
    project?: string;
    category?: string;
    is_client_reported?: boolean;
    state?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }) => {
    const qs = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) qs.set(k, String(v));
      });
    }
    const query = qs.toString();
    return handleInvalidAdminToken(
      await get<GetIssuesResponse>(`${BASE_URL}/admin/issues${query ? `?${query}` : ""}`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminGetCategoryHistory: async () => {
    return handleInvalidAdminToken(
      await get<GetCategoryHistoryResponse>(`${BASE_URL}/admin/issues/categories/history`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminGetPrDashboard: async (refresh = false) => {
    return handleInvalidAdminToken(
      await get<PrDashboardResponse & { error?: string }>(
        `${BASE_URL}/admin/pr-dashboard${refresh ? "?refresh=1" : ""}`,
        {
          method: "GET",
          headers: tokenOnlyHeaders(),
        }
      )
    );
  },
  adminCollectIssues: async () => {
    return handleInvalidAdminToken(
      await get<CollectIssuesResponse>(`${BASE_URL}/admin/issues/collect`, {
        method: "POST",
        headers: jsonHeaders(),
      })
    );
  },
  adminRecalculateCategories: async () => {
    return handleInvalidAdminToken(
      await get<RecalculateCategoriesResponse>(`${BASE_URL}/admin/issues/categories/recalculate`, {
        method: "POST",
        headers: jsonHeaders(),
      })
    );
  },
  adminGetOperatingCosts: async (months: number, refresh: boolean) => {
    return handleInvalidAdminToken(
      await get<OperatingCostsResponse & { error?: string }>(`${BASE_URL}/admin/operating-costs`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ months, refresh }),
      })
    );
  },
  adminSaveOperatingCostEntry: async (entry: OperatingCostEntry) => {
    return handleInvalidAdminToken(
      await get<OperatingCostEntry & { error?: string }>(
        `${BASE_URL}/admin/operating-costs/entries`,
        { method: "POST", headers: jsonHeaders(), body: JSON.stringify(entry) }
      )
    );
  },
  adminDeleteOperatingCostEntry: async (id: string) => {
    return handleInvalidAdminToken(
      await get<{ deleted?: string; error?: string }>(
        `${BASE_URL}/admin/operating-costs/entries/${encodeURIComponent(id)}`,
        { method: "DELETE", headers: jsonHeaders() }
      )
    );
  },
  adminGetUsageTimeseries: async (params: UsageTimeseriesParams) => {
    return handleInvalidAdminToken(
      await get<UsageTimeseriesResponseDTO>(`${BASE_URL}/admin/usage/timeseries`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          start_date: params.startDate,
          end_date: params.endDate,
          granularity: params.granularity,
          agency_id: params.agencyId ?? undefined,
          group_by: params.groupBy ?? undefined,
          include_test_accounts: params.includeTestAccounts ?? false,
        }),
      })
    );
  },
  adminGetUsageOverview: async (params: UsageOverviewParams) => {
    return handleInvalidAdminToken(
      await get<UsageOverviewResponseDTO>(`${BASE_URL}/admin/usage/overview`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          start_date: params.startDate,
          end_date: params.endDate,
          include_test_accounts: params.includeTestAccounts ?? false,
        }),
      })
    );
  },
  adminGetPolicyInsights: async (params: PolicyInsightsParams) => {
    return handleInvalidAdminToken(
      await get<PolicyInsightsResponseDTO>(`${BASE_URL}/admin/usage/policy_insights`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          start_date: params.startDate,
          end_date: params.endDate,
          agency_id: params.agencyId ?? undefined,
          include_test_accounts: params.includeTestAccounts ?? false,
        }),
      })
    );
  },
  adminGetUsageIterations: async (params: UsageIterationsParams) => {
    return handleInvalidAdminToken(
      await get<WorkflowIterationsResponseDTO>(`${BASE_URL}/admin/usage/iterations`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          start_date: params.startDate,
          end_date: params.endDate,
          agency_id: params.agencyId ?? undefined,
          include_test_accounts: params.includeTestAccounts ?? false,
        }),
      })
    );
  },
  adminGetUsageComparison: async (params: UsageComparisonParams) => {
    return handleInvalidAdminToken(
      await get<UsageComparisonResponseDTO>(`${BASE_URL}/admin/usage/compare`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          period_a_start: params.periodAStart,
          period_a_end: params.periodAEnd,
          period_b_start: params.periodBStart,
          period_b_end: params.periodBEnd,
          agency_id: params.agencyId ?? undefined,
          group_by: params.groupBy ?? undefined,
          include_test_accounts: params.includeTestAccounts ?? false,
        }),
      })
    );
  },
  adminAgentTurn: async (sessionId: string, messages: AgentMessage[], chatId?: string | null) => {
    return handleInvalidAdminToken(
      await get<AgentTurnResponse>(`${BASE_URL}/admin/agent/turn`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          messages,
          chat_id: chatId ?? undefined,
        }),
      })
    );
  },
  adminListChats: async () => {
    return handleInvalidAdminToken(
      await get<ListChatsResponse>(`${BASE_URL}/admin/agent/chats`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminGetChat: async (chatId: string) => {
    return handleInvalidAdminToken(
      await get<ChatDetail>(`${BASE_URL}/admin/agent/chats/${chatId}`, {
        method: "GET",
        headers: tokenOnlyHeaders(),
      })
    );
  },
  adminDeleteChat: async (chatId: string) => {
    return handleInvalidAdminToken(
      await get<{ deleted: boolean }>(`${BASE_URL}/admin/agent/chats/${chatId}`, {
        method: "DELETE",
        headers: tokenOnlyHeaders(),
      })
    );
  },
};

export default adminApi;
